/**
 * AdvertiserApiPage — issue + manage opaque API keys for the advertiser API.
 *
 * Layout:
 *   1. Header (green accent, light theme).
 *   2. Issue-key panel — label input, advertiser_id, scopes table, preview
 *      token (client-side), and "Issue" button. On success, one-time reveal.
 *   3. Docs preview — static cURL snippets for POST /conversions and
 *      GET /reports/summary.
 *   4. Active keys table — status, scopes, last used, revoke action.
 *
 * Green accent throughout. Light theme only. No forbidden palette
 * (no emerald, blue, orange, purple, dark, emoji, or competitor refs).
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	ADVERTISER_API_SCOPES,
	generatePreviewToken,
	maskKey,
	toggleScope,
	type AdvertiserApiScope,
} from "./api";
import {
	useAdvertiserApiList,
	useAdvertiserApiCreate,
	useAdvertiserApiDelete,
} from "./hooks";

// --- Tailwind tokens (green family, light theme) ---------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const BTN_PRIMARY =
	"rounded-[6px] border border-green-700 bg-green-700 px-3.5 py-2 text-sm font-medium text-white hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50";
const BTN_DANGER =
	"rounded-[6px] border border-red-300 bg-white px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60";
const HEADLINE =
	"bg-gradient-to-r from-green-700 to-lime-600 bg-clip-text text-3xl font-semibold text-transparent";
const SECTION_LABEL =
	"text-xs font-semibold uppercase tracking-wider text-zinc-500";
const MONO_BOX =
	"rounded-[6px] border border-zinc-200 bg-zinc-50 p-3 text-xs font-mono text-zinc-800 whitespace-pre-wrap break-all";

// --- Component -------------------------------------------------------------

export function AdvertiserApiPage() {
	const listQuery = useAdvertiserApiList();
	const createMut = useAdvertiserApiCreate();
	const deleteMut = useAdvertiserApiDelete();

	const [label, setLabel] = useState("");
	const [advertiserId, setAdvertiserId] = useState("");
	const [scopes, setScopes] = useState<AdvertiserApiScope[]>([
		"conversions:write",
	]);
	const [revealed, setRevealed] = useState<string | null>(null);
	const [revealLabel, setRevealLabel] = useState<string | null>(null);

	// Stable client-only preview token for the "what the key will look like"
	// hint. Regenerates when the user clicks "Shuffle preview".
	const [previewSeed, setPreviewSeed] = useState(0);
	const previewToken = useMemo(
		() => generatePreviewToken(),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[previewSeed],
	);

	const handleIssue = (e: FormEvent) => {
		e.preventDefault();
		if (!label.trim() || scopes.length === 0) return;
		createMut.mutate(
			{
				label: label.trim(),
				advertiser_id: advertiserId.trim() || undefined,
				scopes,
			},
			{
				onSuccess: (row) => {
					setRevealed(row.api_key);
					setRevealLabel(row.label ?? row.id);
					setLabel("");
					setAdvertiserId("");
					setScopes(["conversions:write"]);
				},
			},
		);
	};

	const items = listQuery.data?.items ?? [];
	const activeKeys = items.filter((k) => k.status === "active");

	return (
		<main
			data-testid="advertiser-api-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
		>
			{/* 1. Header ---------------------------------------------------- */}
			<header className="flex flex-col gap-2">
				<p className={SECTION_LABEL}>advertiser-api</p>
				<h1 className={HEADLINE}>Advertiser API keys</h1>
				<p className="max-w-2xl text-sm text-zinc-600">
					Issue scoped API keys your advertisers can use to push
					conversions and pull summary reports directly from their
					backends. Each key is opaque, tenant-bound, and can be
					revoked at any time.
				</p>
			</header>

			{/* 2. Issue panel ----------------------------------------------- */}
			<section className={CARD} aria-label="Issue a new advertiser API key">
				<div className="flex items-center justify-between">
					<h2 className="text-base font-semibold text-zinc-900">
						Issue a new key
					</h2>
					<span className="text-xs text-zinc-500">
						Preview generated locally. Real key is returned only
						once on issue.
					</span>
				</div>

				<form
					onSubmit={handleIssue}
					className="mt-4 flex flex-col gap-4"
					data-testid="advertiser-api-issue-form"
				>
					<div className="grid gap-4 sm:grid-cols-2">
						<label className="flex flex-col gap-1 text-sm text-zinc-700">
							<span className="font-medium">Label</span>
							<input
								type="text"
								className={INPUT}
								placeholder="e.g. Acme Corp production"
								value={label}
								onChange={(e) => setLabel(e.currentTarget.value)}
								disabled={createMut.isPending}
								data-testid="advertiser-api-label-input"
								maxLength={80}
							/>
						</label>
						<label className="flex flex-col gap-1 text-sm text-zinc-700">
							<span className="font-medium">Advertiser ID (optional)</span>
							<input
								type="text"
								className={INPUT}
								placeholder="adv_xxxxx (auto-assigned if blank)"
								value={advertiserId}
								onChange={(e) =>
									setAdvertiserId(e.currentTarget.value)
								}
								disabled={createMut.isPending}
								data-testid="advertiser-api-advertiser-input"
								maxLength={128}
							/>
						</label>
					</div>

					{/* Scopes table */}
					<fieldset
						className="flex flex-col gap-2"
						data-testid="advertiser-api-scopes"
					>
						<legend className="text-sm font-medium text-zinc-700">
							Scopes
						</legend>
						<div className="overflow-hidden rounded-[6px] border border-zinc-200">
							<table className="min-w-full divide-y divide-zinc-200 text-sm">
								<thead className="bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500">
									<tr>
										<th className="w-12 px-3 py-2"> </th>
										<th className="px-3 py-2">Scope</th>
										<th className="px-3 py-2">Description</th>
									</tr>
								</thead>
								<tbody className="divide-y divide-zinc-100 bg-white">
									{ADVERTISER_API_SCOPES.map((s) => {
										const checked = scopes.includes(s.id);
										const toggleId = `advertiser-api-scope-${s.id}`;
										return (
											<tr key={s.id}>
												<td className="px-3 py-2 align-top">
													<input
														type="checkbox"
														id={toggleId}
														checked={checked}
														onChange={() =>
															setScopes((prev) =>
																toggleScope(prev, s.id),
															)
														}
														disabled={createMut.isPending}
														className="h-4 w-4 rounded border-zinc-300 text-green-700 focus:ring-green-600"
														data-testid={`advertiser-api-scope-toggle-${s.id}`}
													/>
												</td>
												<td className="px-3 py-2 align-top">
													<label
														htmlFor={toggleId}
														className="font-mono text-xs text-zinc-800"
													>
														{s.label}
													</label>
												</td>
												<td className="px-3 py-2 align-top text-xs text-zinc-600">
													{s.description}
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						</div>
						{scopes.length === 0 ? (
							<p
								className="text-xs text-red-600"
								role="alert"
								data-testid="advertiser-api-scope-error"
							>
								Select at least one scope.
							</p>
						) : null}
					</fieldset>

					{/* Preview token */}
					<div className="flex flex-col gap-2">
						<div className="flex items-center justify-between">
							<p className="text-sm font-medium text-zinc-700">
								Preview format
							</p>
							<button
								type="button"
								className={BTN_GHOST}
								onClick={() => setPreviewSeed((n) => n + 1)}
								data-testid="advertiser-api-shuffle-preview"
							>
								Shuffle preview
							</button>
						</div>
						<div
							className={MONO_BOX}
							data-testid="advertiser-api-preview-token"
						>
							{previewToken}
						</div>
						<p className="text-xs text-zinc-500">
							This is a local preview only. Real keys are
							generated server-side and shown once.
						</p>
					</div>

					<div className="flex items-center justify-between pt-2">
						<button
							type="submit"
							className={BTN_PRIMARY}
							disabled={
								createMut.isPending ||
								!label.trim() ||
								scopes.length === 0
							}
							data-testid="advertiser-api-submit"
						>
							{createMut.isPending ? "Issuing." : "Issue key"}
						</button>
						{createMut.error ? (
							<p
								className="text-xs text-red-600"
								role="alert"
								data-testid="advertiser-api-issue-error"
							>
								{createMut.error.message}
							</p>
						) : null}
					</div>
				</form>

				{/* One-time reveal */}
				{revealed ? (
					<div
						className="mt-5 rounded-[6px] border border-green-200 bg-green-50 p-4"
						role="status"
						data-testid="advertiser-api-reveal"
					>
						<p className="text-sm font-semibold text-green-900">
							Key issued{revealLabel ? `: ${revealLabel}` : ""}
						</p>
						<p className="mt-1 text-xs text-green-800">
							Copy it now. You will not see the full key again.
						</p>
						<div className={`${MONO_BOX} mt-3`}>{revealed}</div>
						<div className="mt-2 flex items-center gap-3">
							<span
								className="font-mono text-xs text-zinc-500"
								data-testid="advertiser-api-reveal-mask"
							>
								Stored as: {maskKey(revealed)}
							</span>
							<button
								type="button"
								className={BTN_GHOST}
								onClick={() => {
									setRevealed(null);
									setRevealLabel(null);
								}}
								data-testid="advertiser-api-reveal-dismiss"
							>
								Dismiss
							</button>
						</div>
					</div>
				) : null}
			</section>

			{/* 3. Docs preview ---------------------------------------------- */}
			<section className={CARD} aria-label="Advertiser API docs preview">
				<h2 className="text-base font-semibold text-zinc-900">
					Quickstart
				</h2>
				<p className="mt-1 text-sm text-zinc-600">
					Advertisers authenticate with the{" "}
					<code className="rounded bg-zinc-100 px-1 py-0.5 font-mono text-xs">
						x-advertiser-api-key
					</code>{" "}
					header. No OAuth, no session. Endpoints:
				</p>

				<div className="mt-4 grid gap-4 sm:grid-cols-2">
					<div>
						<p className={SECTION_LABEL}>POST /api/v1/advertiser-api/conversions</p>
						<pre className={`${MONO_BOX} mt-2`} data-testid="advertiser-api-docs-post">
{`curl -X POST \\
  https://api.example.com/api/v1/advertiser-api/conversions \\
  -H "x-advertiser-api-key: adv_xxxxxxxxxxxx" \\
  -H "content-type: application/json" \\
  -d '{
    "click_id": "clk_abc123",
    "amount_cents": 2499,
    "currency": "USD",
    "event_name": "purchase"
  }'`}
						</pre>
					</div>
					<div>
						<p className={SECTION_LABEL}>GET /api/v1/advertiser-api/reports/summary</p>
						<pre className={`${MONO_BOX} mt-2`} data-testid="advertiser-api-docs-get">
{`curl -G \\
  https://api.example.com/api/v1/advertiser-api/reports/summary \\
  -H "x-advertiser-api-key: adv_xxxxxxxxxxxx" \\
  --data-urlencode "from=1714521600" \\
  --data-urlencode "to=1717113600"`}
						</pre>
					</div>
				</div>
			</section>

			{/* 4. Active keys table ----------------------------------------- */}
			<section className={CARD} aria-label="Active advertiser API keys">
				<div className="flex items-center justify-between">
					<h2 className="text-base font-semibold text-zinc-900">
						Active keys
					</h2>
					<span className="text-xs text-zinc-500">
						{activeKeys.length}{" "}
						{activeKeys.length === 1 ? "key" : "keys"}
					</span>
				</div>

				{listQuery.isLoading ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="advertiser-api-loading"
					>
						Loading.
					</p>
				) : listQuery.error ? (
					<p
						className="mt-3 text-sm text-red-600"
						role="alert"
						data-testid="advertiser-api-list-error"
					>
						{listQuery.error.message}
					</p>
				) : activeKeys.length === 0 ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="advertiser-api-empty"
					>
						No active keys yet. Issue one above.
					</p>
				) : (
					<div
						className="mt-3 overflow-hidden rounded-[6px] border border-zinc-200"
						data-testid="advertiser-api-list"
					>
						<table className="min-w-full divide-y divide-zinc-200 text-sm">
							<thead className="bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500">
								<tr>
									<th className="px-3 py-2">Label</th>
									<th className="px-3 py-2">Advertiser</th>
									<th className="px-3 py-2">Scopes</th>
									<th className="px-3 py-2">Last used</th>
									<th className="px-3 py-2"> </th>
								</tr>
							</thead>
							<tbody className="divide-y divide-zinc-100 bg-white">
								{activeKeys.map((row) => (
									<tr
										key={row.id}
										data-testid={`advertiser-api-row-${row.id}`}
									>
										<td className="px-3 py-2 text-zinc-900">
											{row.label ?? (
												<span className="text-zinc-400">
													(no label)
												</span>
											)}
										</td>
										<td className="px-3 py-2 font-mono text-xs text-zinc-700">
											{row.advertiser_id}
										</td>
										<td className="px-3 py-2">
											<div className="flex flex-wrap gap-1">
												{row.scopes.map((s) => (
													<span
														key={s}
														className="rounded border border-green-200 bg-green-50 px-1.5 py-0.5 font-mono text-xs text-green-800"
													>
														{s}
													</span>
												))}
											</div>
										</td>
										<td className="px-3 py-2 text-xs text-zinc-500">
											{row.last_used_at
												? new Date(
														row.last_used_at * 1000,
													).toISOString()
												: "never"}
										</td>
										<td className="px-3 py-2 text-right">
											<button
												type="button"
												className={BTN_DANGER}
												onClick={() => deleteMut.mutate(row.id)}
												disabled={deleteMut.isPending}
												data-testid={`advertiser-api-revoke-${row.id}`}
											>
												Revoke
											</button>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</section>
		</main>
	);
}

export default AdvertiserApiPage;
